import * as React from 'react';
import { observer } from 'mobx-react';
import { FormField } from './form-field';
import { FormControlLabel } from '../primitives/label';
import { Textbox } from '../primitives/textbox';
import { TextMaskFn, testRegExp } from '../utils/text-mask';
import { TextMaskInput } from './text-mask-input';
import { FieldValidationMessage } from './form-control/form-control-validation-error';

const firstCharMaskFn: TextMaskFn = (char, oldState, nextChars) => {
	// 1. Adding 0 when typing / after entering a single number
	// Only when there's no trailing characters

	// 2. Adding 0 if the only input is 2-9
	// This only happens when the oldState value is an empty string.
	// It's really annoying otherwise, preventing a user from deleting characters and jumping

	if (oldState && (
		(oldState.value === char && /[1-9]/.test(char) && nextChars[0] === '/') ||
		(oldState && !oldState.value && /[2-9]/.test(char)))
	) {
		return { char: `0${char}` };
	}

	return testRegExp(/[0-9]/, char);
};

const disallowDoubleZero: TextMaskFn = (char, oldState, nextChars, previousChars) => {
	if (char === '0' && previousChars.reverse()[0] === '0') {
		return { char: '' };
	}

	return testRegExp(/[0-9]/, char);
};

export const expiryDateMask = [firstCharMaskFn, disallowDoubleZero, ' ', '/', ' ', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];

export interface ICardNumberInputProps {
	value: FormField<string>;
	formControlId: string;
	acceptanceTestTargetId: string;
}

@observer
export class ExpiryDateInput extends React.Component<ICardNumberInputProps> {

	// The extra month/year fields are for Safari autofill, since it cannot properly
	// fill a single field.
	private monthAutofillField: HTMLInputElement;
	private yearAutofillField: HTMLInputElement;

	private expiryYear: string;
	private expiryMonth: string;

	render() {
		const { value, formControlId, acceptanceTestTargetId } = this.props;
		const additionalClassNames = value.hasValidationError ? 'form-control-invalid' : '';
		return (
			<div className="form-control-container">
				<FormControlLabel formControlId={formControlId}>Expiration</FormControlLabel>
				<input type="text"
					id={`${formControlId}-month`}
					autoComplete="cc-exp-month"
					maxLength={2}
					onChange={this.onAutofill}
					ref={this.setMonthAutofillField}
					tabIndex={-1}
					aria-hidden={true}
					className="user-hidden"
					/>
				<input type="text"
					id={`${formControlId}-year`}
					autoComplete="cc-exp-year"
					onChange={this.onAutofill}
					ref={this.setYearAutofillField}
					tabIndex={-1}
					aria-hidden={true}
					className="user-hidden"
					/>
				<TextMaskInput mask={expiryDateMask}>
					<Textbox
						value={value.value}
						formControlId={formControlId}
						placeholder="MM / YY"
						disableTranslation={true}
						onChange={value.updateValue}
						onBlur={this.cleanValue}
						onKeyDown={this.prefixIfNeeded}
						numericKeyboard
						dataFieldInvalid={value.hasValidationError}
						additionalClassNames={additionalClassNames}
						acceptanceTestTargetId={acceptanceTestTargetId} />
				</TextMaskInput>
				<FieldValidationMessage errorMessage={value.validationError} />
			</div>
		);
	}

	private setMonthAutofillField = (el: HTMLInputElement) => this.monthAutofillField = el;
	private setYearAutofillField = (el: HTMLInputElement) => this.yearAutofillField = el;

	private onAutofill = (target: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = this.props;
		this.expiryMonth = this.monthAutofillField.value || this.expiryMonth;
		this.expiryYear = this.yearAutofillField.value || this.expiryYear;
		const expiryDate = `${this.expiryMonth} / ${this.expiryYear}`;
		value.updateValue(this.removeYearCentury(expiryDate));
		this.monthAutofillField.value = '';
		this.yearAutofillField.value = '';
	}

	private prefixIfNeeded = (target: React.KeyboardEvent<HTMLInputElement>) => {
		const { value } = this.props;
		if (target.charCode === '/'.charCodeAt(0) && value.value.match(/^\d$/)) {
			value.updateValue('0' + value.value);
		}
	}

	private cleanValue = () => {
		const {value} = this.props;
		value.updateValue(this.removeYearCentury(value.value));
		value.revalidate();
	}

	// Browser autocomplete will force a fill year in to the expiry field, so remove the 20 prefix.
	// This is not Y2100 ready.
	private removeYearCentury(value: string) {
		const matches = /^(.*)20(\d{2})$/.exec(value);
		if (matches) {
			return matches[1] + matches[2];
		}
		return value;
	}
}
